import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { Spinner } from "@dearbill/shared";
import { useOrg } from "./OrgContext";
import { usePartnerMe } from "../auth/usePartnerMe";
import { usePharmacyOnboarding } from "../components/onboarding/PharmacyOnboardingContext";

/**
 * Only a bare org entry opens an unstarted pharmacy introduction automatically.
 * Deep links stay reachable regardless of introduction or activation state.
 * An unavailable introduction never blocks the existing organization destination.
 */
export function OrgIndexRedirect(): ReactNode {
  const { orgId, res } = useOrg();
  const { settled } = usePartnerMe();
  const onboarding = usePharmacyOnboarding();
  const isPharmacy = res.profile.organizationType === "Pharmacy";
  if (isPharmacy && (!settled || onboarding?.loading)) {
    return (
      <div className="space-y-4">
        <p role="status" className="flex items-center gap-3">
          <Spinner /> Loading your introduction...
        </p>
        <Link
          to={`/orgs/${orgId}/home`}
          className="text-ember inline-flex min-h-11 items-center underline"
        >
          Go to Home
        </Link>
      </div>
    );
  }
  if (isPharmacy && onboarding?.progress?.status === "not_started" && !onboarding.loadError) {
    return <Navigate to="get-started" replace />;
  }
  const setupNeeded =
    res.blockers.includes("profile_incomplete") || res.blockers.includes("document_unsigned");
  const target = setupNeeded ? "organization" : "home";
  return <Navigate to={target} replace />;
}
